import type { Player } from "../../../common/types.ts";
import { PLAYER } from "../../../common/constants.ts";
import { g } from "../../util/index.ts";
import moodComponents from "../player/moodComponents.ts";
import getPayroll from "../team/getPayroll.ts";
import { idb } from "../../db/index.ts";

// How much a player would like a team, counting only what his traits care about: market size and hype (F), facilities
// and payroll ($), team performance (W) and loyalty to his current team (L). Players without traits are neutral.
export const affinityScore = (
	components: Awaited<ReturnType<typeof moodComponents>>,
	traits: string[],
	wealth: number,
) =>
	(traits.includes("F") ? components.marketSize + components.hype : 0) +
	(traits.includes("$") ? components.facilities + 1.5 * wealth : 0) +
	(traits.includes("W") ? components.teamPerformance : 0) +
	(traits.includes("L") ? components.loyalty : 0);

// Teams the player prefers are cheaper to trade him to (up to 15% off for the team giving him away, up to 10% more for
// the team getting him); a player who would rather stay costs a bit more to pry loose.
export const affinityFactor = (diff: number, incoming: boolean) => {
	const t = Math.tanh(Math.abs(diff) / 5);
	if (incoming) {
		return diff >= 0 ? 1 + 0.1 * t : 1 - 0.05 * t;
	}
	return diff >= 0 ? 1 - 0.15 * t : 1 + 0.05 * t;
};

export const currentTid = (p: Player) =>
	p.tid === PLAYER.MINORS ? p.minorTid : p.tid;

// Wealth rank by what each club actually spends (current payroll, market size breaking ties), from -2 (poorest) to 2
// (richest). Not the AI payroll plan: the user's club has none, and before the first plan of a season every club gets
// the same default, which ranked clubs by team id (the Yankees came out poorer than Arizona).
export const wealthRanks = (teams: { tid: number; payroll: number; pop: number }[]) => {
	const sorted = [...teams].sort((a, b) => a.payroll - b.payroll || a.pop - b.pop || a.tid - b.tid);
	const wealth = new Map<number, number>();
	for (const [i, row] of sorted.entries()) {
		wealth.set(row.tid, sorted.length > 1 ? -2 + (4 * i) / (sorted.length - 1) : 0);
	}
	return wealth;
};

export const loadWealth = async () => {
	const teams = (await idb.cache.teams.getAll()).filter((t) => !t.disabled);
	return wealthRanks(
		await Promise.all(teams.map(async (t) => ({ tid: t.tid, payroll: await getPayroll(t.tid), pop: t.pop ?? 0 }))),
	);
};

export const affinity = async (p: Player, tid: number, wealth: Map<number, number>) => {
	const traits = g.get("playerMoodTraits") ? p.moodTraits : [];
	return affinityScore(await moodComponents(p, tid), traits, wealth.get(tid) ?? 0);
};
